import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { breathPatternFor } from '@goaltree/backend/convex/lib/breathwork'
import { HOUR_MS } from '@goaltree/backend/convex/lib/fasting'
import type { LifeCategory } from '@goaltree/backend/convex/lib/habitCategory'
import { isNewBest, overTarget, tracksDayBest } from '@goaltree/backend/convex/lib/habitDone'
import {
  FAST_PRESETS,
  type FastPresetId,
  type HabitMeasure,
  MAX_FAST_HOURS,
  MAX_QUICK_ADDS,
  MAX_TIMER_MINUTES,
  MAX_UNIT_LENGTH,
  MEASURE_PRESETS,
  type MeasurePresetId,
  cleanMeasure,
  dayAmount,
  fastMeasure,
  formatAmount,
  isBestOfDay,
  isFast,
  isMeasureMet,
  isMultiDayFast,
  isStepper as isStepperMeasure,
  isTimerMeasure,
  isValidAmount,
  presetsToSuggest,
  roundAmount,
} from '@goaltree/backend/convex/lib/habitMeasure'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { Drawer, DrawerContent, DrawerTitle } from '@goaltree/ui/components/drawer'
import { Popover, PopoverContent, PopoverTrigger } from '@goaltree/ui/components/popover'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import type { TFunction } from 'i18next'
import { Check, Hash, Play, Plus, Square, Timer, Trash2, Trophy, Wind, X } from 'lucide-react'
import {
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { BreathPacer, pacerSkipped, setPacerSkipped } from '@/components/breath-pacer'
import { habitIconComponent } from '@/components/habit-icon'
import { NewBestChip, useHabitShareState } from '@/components/share-card'
import { useCloseOnOverlayTap } from '@/hooks/use-close-on-overlay-tap'
import { useIsMobile } from '@/hooks/use-mobile'
import { CATEGORY_COLORS, useCategoryFill } from '@/lib/category-color'
import { patchHabit } from '@/lib/goals-optimistic'
import { useHabitAccent } from '@/lib/habit-accent'
import { haptic } from '@/lib/haptics'
import { playBellSound, playDoneSound } from '@/lib/sounds'
import { parseTaskFields } from '@/lib/tags'
import { undoToast } from '@/lib/undo-toast'

export type MeasureProgress = {
  measure: HabitMeasure
  total: number
  count: number
  title: string
  fast?: { startedAt: number } | null
  timer?: { startedAt: number; minutes: number | null } | null
}

const fmt = (n: number, lang: string) => formatAmount(n, lang)
// Best-of-day habits log one attempt per entry, so never as +1 segments.
const isStepper = (m: HabitMeasure) => isStepperMeasure(m) && !isBestOfDay(m)

// Today's total for a number habit, from the shared getHabits subscription.
export function useHabitMeasure(habitId: string | null | undefined): MeasureProgress | null {
  const habits = useQuery(api.nodes.getHabits, habitId ? {} : 'skip')
  return useMemo(() => {
    const h = habits?.find((x) => x._id === habitId)
    if (!h?.measure) return null
    return {
      measure: h.measure,
      total: h.measureToday?.total ?? 0,
      count: h.measureToday?.count ?? 0,
      title: parseTaskFields(h.title).title,
      fast: h.fast,
      timer: h.timer,
    }
  }, [habits, habitId])
}

export function useLogAmount() {
  return useMutation(api.habitLogs.logAmount).withOptimisticUpdate((store, { habitId, amount }) => {
    const habits = store.getQuery(api.nodes.getHabits, {})
    if (!habits) return
    store.setQuery(
      api.nodes.getHabits,
      {},
      habits.map((h) =>
        h._id === habitId && h.measure
          ? {
              ...h,
              measureToday: {
                date: h.measureToday?.date ?? '',
                total: roundAmount(
                  isBestOfDay(h.measure)
                    ? Math.max(h.measureToday?.total ?? 0, amount)
                    : (h.measureToday?.total ?? 0) + amount,
                ),
                count: (h.measureToday?.count ?? 0) + 1,
              },
            }
          : h,
      ),
    )
  })
}

function statusLine(t: TFunction, m: HabitMeasure, total: number, count: number, lang: string) {
  const diff = fmt(Math.abs(m.target - total), lang)
  if (m.direction === 'atLeast')
    return total >= m.target
      ? { text: t('numberHabit.doneHit'), tone: 'done' as const }
      : { text: t('numberHabit.toGo', { amount: diff, unit: m.unit }), tone: 'muted' as const }
  if (total > m.target)
    return { text: t('numberHabit.over', { amount: diff, unit: m.unit }), tone: 'over' as const }
  return {
    text: count
      ? t('numberHabit.left', { amount: diff, unit: m.unit })
      : t('numberHabit.upTo', { amount: fmt(m.target, lang), unit: m.unit }),
    tone: 'muted' as const,
  }
}

export function NumberHabitProgress({
  measure,
  total,
  count,
  habitId,
  className,
}: {
  measure: HabitMeasure
  total: number
  count: number
  // Enables the "New best" chip once the target is met.
  habitId?: string
  className?: string
}) {
  const { t, i18n } = useTranslation()
  const over = measure.direction === 'atMost' && total > measure.target
  const met = isMeasureMet(measure, total, count)
  const extra = overTarget(measure, total)
  const line = statusLine(t, measure, total, count, i18n.language)
  const share = useHabitShareState(
    habitId,
    met && !!extra && tracksDayBest(measure) && !isBestOfDay(measure),
  )
  const newBest = !!extra && !isBestOfDay(measure) && isNewBest(total, share?.prevBest)
  const fill = over
    ? 'bg-amber-500'
    : met
      ? 'bg-[var(--habit-accent,var(--primary))]'
      : 'bg-[color-mix(in_srgb,var(--habit-accent,var(--primary))_70%,transparent)]'
  return (
    <div className={cn('min-w-0', className)}>
      <p className="flex items-center gap-1 font-mono text-[13px] font-semibold leading-none tabular-nums text-foreground">
        <span>
          {isBestOfDay(measure) && (
            <span className="font-sans text-[11px] font-medium text-muted-foreground">
              {t('numberHabit.best')}{' '}
            </span>
          )}
          {fmt(total, i18n.language)}
          <span className="font-medium text-muted-foreground">
            {' '}
            / {fmt(measure.target, i18n.language)} {measure.unit}
          </span>
        </span>
        {met && (
          <Check
            aria-hidden
            className="size-3.5 shrink-0 text-[var(--habit-accent,var(--primary-text))]"
            strokeWidth={3}
          />
        )}
      </p>
      {isStepper(measure) ? (
        <div className="mt-1.5 flex gap-1" aria-hidden>
          {Array.from({ length: measure.target }, (_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 flex-1 rounded-full transition-colors',
                i < total ? fill : 'bg-foreground/[0.08]',
              )}
            />
          ))}
        </div>
      ) : (
        <div
          className="relative mt-1.5 h-1.5 overflow-hidden rounded-full bg-foreground/[0.08]"
          aria-hidden
        >
          <div
            className={cn('h-full rounded-full transition-[width] duration-300', fill)}
            style={{ width: `${Math.min(100, (total / measure.target) * 100)}%` }}
          />
        </div>
      )}
      <p
        className={cn(
          'mt-1 flex items-center gap-1.5 font-mono text-[11px] leading-none',
          line.tone === 'over'
            ? 'text-amber-600 dark:text-amber-400'
            : line.tone === 'done'
              ? 'text-[var(--habit-accent,var(--primary-text))]'
              : 'text-muted-foreground',
        )}
      >
        {extra?.extra
          ? t('numberHabit.extra', { amount: fmt(extra.extra, i18n.language) })
          : line.text}
        {newBest && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/15 px-1.5 py-0.5 font-sans text-[10px] font-bold text-amber-700 dark:text-amber-300">
            <Trophy className="size-2.5" />
            {t('shareCard.newBest')}
          </span>
        )}
      </p>
    </div>
  )
}

// Quick amounts under the + (long-press, right-click or its chevron): the
// habit's quick adds and "Custom…" for the log sheet.
function QuickAmountMenu({
  anchor,
  measure,
  onPick,
  onCustom,
  onClose,
}: {
  anchor: DOMRect
  measure: HabitMeasure
  onPick: (amount: number) => void
  onCustom: () => void
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const away = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose()
    }
    document.addEventListener('pointerdown', away, true)
    document.addEventListener('keydown', key, true)
    window.addEventListener('scroll', onClose, true)
    window.addEventListener('resize', onClose)
    return () => {
      document.removeEventListener('pointerdown', away, true)
      document.removeEventListener('keydown', key, true)
      window.removeEventListener('scroll', onClose, true)
      window.removeEventListener('resize', onClose)
    }
  }, [onClose])
  const best = isBestOfDay(measure)
  const below = anchor.bottom + 180 < window.innerHeight
  const item =
    'flex h-9 w-full cursor-pointer items-center rounded-lg px-3 text-left font-mono text-sm font-semibold tabular-nums text-foreground transition-colors hover:bg-primary/10'
  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={t('numberHabit.quickAmounts')}
      {...rowQuiet}
      style={{
        position: 'fixed',
        right: Math.max(8, window.innerWidth - anchor.right),
        ...(below ? { top: anchor.bottom + 6 } : { bottom: window.innerHeight - anchor.top + 6 }),
      }}
      className="z-50 min-w-36 rounded-xl border border-border/60 bg-popover p-1 shadow-lg animate-in fade-in-0 zoom-in-95"
    >
      <p className="px-3 pt-1.5 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
        {t('numberHabit.logMore')}
      </p>
      {measure.quickAdds.map((q) => (
        <button
          key={q}
          type="button"
          role="menuitem"
          onClick={() => {
            onClose()
            onPick(q)
          }}
          className={item}
        >
          {best ? '' : '+'}
          {fmt(q, i18n.language)}
          <span className="ml-1.5 font-medium text-muted-foreground">{measure.unit}</span>
        </button>
      ))}
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onClose()
          onCustom()
        }}
        className={cn(item, 'font-sans')}
      >
        {t('numberHabit.customAmount')}
      </button>
    </div>,
    document.body,
  )
}

const LONG_PRESS_MS = 450

function AddButton({
  measure,
  title,
  met,
  onClick,
  onQuick,
  onCustom,
}: {
  measure: HabitMeasure
  title: string
  met: boolean
  onClick: () => void
  onQuick?: (amount: number) => void
  onCustom?: () => void
}) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const quick = !!onQuick && !!onCustom
  const [menu, setMenu] = useState<DOMRect | null>(null)
  const hold = useRef<ReturnType<typeof setTimeout> | null>(null)
  const held = useRef(false)
  const openMenu = () => {
    held.current = true
    haptic('light')
    setMenu(ref.current?.getBoundingClientRect() ?? null)
  }
  const release = () => {
    if (hold.current) clearTimeout(hold.current)
    hold.current = null
  }
  const closeMenu = useCallback(() => setMenu(null), [])
  const label = met ? t('numberHabit.logMore') : t('numberHabit.logAria', { title })
  return (
    <div ref={ref} className="flex shrink-0 items-center">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          if (held.current) {
            held.current = false
            return
          }
          onClick()
        }}
        onPointerDown={(e) => {
          e.stopPropagation()
          held.current = false
          if (quick && e.pointerType !== 'mouse') hold.current = setTimeout(openMenu, LONG_PRESS_MS)
        }}
        onPointerUp={release}
        onPointerLeave={release}
        onPointerCancel={release}
        onContextMenu={(e) => {
          e.stopPropagation()
          if (!quick) return
          e.preventDefault()
          release()
          if (!menu) openMenu()
        }}
        onTouchStart={(e) => e.stopPropagation()}
        aria-label={label}
        title={label}
        className="flex size-11 shrink-0 cursor-pointer select-none items-center justify-center rounded-full border border-primary/30 bg-primary/10 font-mono text-sm font-bold text-primary-text transition-colors [-webkit-touch-callout:none] hover:bg-primary/20 active:scale-95"
      >
        {isStepper(measure) ? '+1' : <Plus className="size-5" strokeWidth={2.5} />}
      </button>
      {menu && onQuick && onCustom && (
        <QuickAmountMenu
          anchor={menu}
          measure={measure}
          onPick={onQuick}
          onCustom={onCustom}
          onClose={closeMenu}
        />
      )}
    </div>
  )
}

// Number-habit rows: `add` is the + action (+1 logs, otherwise the log sheet
// opens); `node` is the progress strip with the 44px button and the sheet;
// `quickAdd` logs the first quick amount with an undo (swipe on a done row).
export function useNumberHabitRow(habitId: Id<'nodes'> | string, className?: string) {
  const { t, i18n } = useTranslation()
  const p = useHabitMeasure(habitId as string)
  const [open, setOpen] = useState(false)
  const quickLog = useQuickLog()
  const deleteEntry = useDeleteEntry()
  const fast = useFastActions()
  const timer = useTimerActions()
  const accent = useHabitAccent(habitId as string)
  if (!p) return { progress: null, add: () => {}, quickAdd: null, node: null }
  const id = habitId as Id<'nodes'>
  if (isFast(p.measure)) {
    return {
      progress: p,
      add: () => void (p.fast ? fast.end(id, p) : fast.start(id)),
      quickAdd: null,
      node: <FastHabitRow habitId={id} progress={p} actions={fast} className={className} />,
    }
  }
  const add = () => (isStepper(p.measure) ? void quickLog(id, p, 1) : setOpen(true))
  const timed = isTimerMeasure(p.measure)
  const met = isMeasureMet(p.measure, p.total, p.count)
  const first = p.measure.quickAdds[0]
  const quickAdd =
    timed || !first
      ? null
      : async () => {
          const r = await quickLog(id, p, first)
          if (!r) return
          const amount = `${isBestOfDay(p.measure) ? '' : '+'}${fmt(first, i18n.language)}`
          undoToast(
            t('numberHabit.added', { amount, unit: p.measure.unit }),
            () =>
              void deleteEntry({ entryId: r.entryId }).catch(() =>
                toast.error(t('numberHabit.logError')),
              ),
          )
        }
  const node = (
    <div
      className={cn('flex items-center gap-3', className)}
      style={accent ? ({ '--habit-accent': accent } as CSSProperties) : undefined}
    >
      {timed ? (
        <TimerSection habitId={id} progress={p} actions={timer} onLog={add} />
      ) : (
        <NumberHabitProgress
          measure={p.measure}
          total={p.total}
          count={p.count}
          habitId={habitId as string}
          className="flex-1"
        />
      )}
      {isBestOfDay(p.measure) && !p.timer && <NewBestChip habitId={habitId} />}
      {!timed && (
        <AddButton
          measure={p.measure}
          title={p.title}
          met={met}
          onClick={add}
          onQuick={(amount) => void quickLog(id, p, amount)}
          onCustom={() => setOpen(true)}
        />
      )}
      <NumberLogSheet open={open} onOpenChange={setOpen} habitId={id} progress={p} />
    </div>
  )
  return { progress: p, add, quickAdd, node }
}

export function NumberHabitStrip({ habitId, className }: { habitId: string; className?: string }) {
  return useNumberHabitRow(habitId, className).node
}

export function useQuickLog() {
  const { t } = useTranslation()
  const log = useLogAmount()
  return async (
    habitId: Id<'nodes'>,
    p: Pick<MeasureProgress, 'measure' | 'total' | 'count'>,
    amount: number,
    extra?: { note?: string; source?: 'manual' | 'photo' },
  ) => {
    const wasMet = isMeasureMet(p.measure, p.total, p.count)
    haptic('light')
    try {
      const r = await log({
        habitId,
        amount,
        tzOffset: new Date().getTimezoneOffset(),
        ...extra,
      })
      if (r.done && !wasMet) {
        haptic('success')
        playDoneSound()
      }
      return r
    } catch {
      toast.error(t('numberHabit.logError'))
      return null
    }
  }
}

const stop = (event: { stopPropagation: () => void }) => event.stopPropagation()

// Portal events bubble through React to the habit row. Stopping them here keeps a
// tap, swipe, or long-press on the sheet from opening the row or starting its swipe.
const rowQuiet = {
  onClick: stop,
  onPointerDown: stop,
  onPointerMove: stop,
  onPointerUp: stop,
  onTouchStart: stop,
  onTouchMove: stop,
  onTouchEnd: stop,
  onContextMenu: stop,
}

const HABIT_DRAWER_CLASS =
  'mx-auto w-full max-w-lg rounded-t-2xl data-[vaul-drawer-direction=bottom]:max-h-[90dvh] data-[vaul-drawer-direction=bottom]:rounded-t-2xl'

export function HabitDrawer({
  open,
  onOpenChange,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}) {
  useCloseOnOverlayTap(open, onOpenChange)
  return (
    <div className="contents" {...rowQuiet}>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className={HABIT_DRAWER_CLASS}>
          {/* iOS claims a vertical pan on the element under the finger. touch-action
              is not inherited from the drawer, so the body opts out (see index.css)
              and a swipe here dismisses. */}
          <div
            data-habit-sheet=""
            className="px-5 pt-4 pb-[max(env(safe-area-inset-bottom),20px)] text-foreground"
          >
            {children}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  )
}

function useDeleteEntry() {
  return useMutation(api.habitLogs.deleteEntry).withOptimisticUpdate((store, { entryId }) => {
    for (const { args, value } of store.getAllQueries(api.habitLogs.dayEntries)) {
      if (!value?.entries.some((e) => e._id === entryId)) continue
      const aggregate = store.getQuery(api.nodes.getHabits, {})?.find((h) => h._id === args.habitId)
        ?.measure?.aggregate
      const entries = value.entries.filter((e) => e._id !== entryId)
      const total = roundAmount(
        dayAmount(
          entries.map((e) => e.amount),
          aggregate,
        ),
      )
      store.setQuery(api.habitLogs.dayEntries, args, { ...value, entries, total })
      patchHabit(store, args.habitId, (h) =>
        h.measureToday && h.measureToday.date === value.date
          ? {
              measureToday: {
                ...h.measureToday,
                total,
                count: Math.max(0, h.measureToday.count - 1),
              },
            }
          : {},
      )
    }
  })
}

export function NumberLogSheet({
  open,
  onOpenChange,
  habitId,
  progress,
  initialAmount,
  initialNote,
  source,
  onLogged,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  habitId: Id<'nodes'>
  progress: MeasureProgress
  initialAmount?: number
  initialNote?: string
  source?: 'manual' | 'photo'
  onLogged?: () => void
}) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobile()
  const { measure, total, count, title } = progress
  const [value, setValue] = useState('')
  const [note, setNote] = useState('')
  const quickLog = useQuickLog()
  const day = useQuery(api.habitLogs.dayEntries, open ? { habitId } : 'skip')
  const deleteEntry = useDeleteEntry()

  // Capture phase: a keydown handler on a row this sheet renders inside (React
  // bubbles portal events through it) can't swallow Escape before the sheet sees it.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.isComposing) return
      e.stopPropagation()
      onOpenChange(false)
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open) return
    setValue(initialAmount ? String(initialAmount) : '')
    setNote(initialNote ?? '')
  }, [open, initialAmount, initialNote])

  const amount = Number(value.replace(',', '.'))
  const valid = value !== '' && isValidAmount(amount)
  const best = isBestOfDay(measure)
  const after = roundAmount(
    best ? Math.max(total, valid ? amount : 0) : total + (valid ? amount : 0),
  )
  const line = statusLine(t, measure, after, count + (valid ? 1 : 0), i18n.language)
  const lang = i18n.language

  const submit = async () => {
    if (!valid) return
    onOpenChange(false)
    const r = await quickLog(habitId, progress, amount, {
      ...(note.trim() && { note: note.trim() }),
      ...(source && { source }),
    })
    if (r) onLogged?.()
  }

  const body = (
    <div className="flex flex-col gap-4">
      <div className="pr-8">
        {isMobile ? (
          <DrawerTitle className="text-base font-extrabold">
            {t('numberHabit.logTitle', { title })}
          </DrawerTitle>
        ) : (
          <DialogTitle className="text-base font-extrabold">
            {t('numberHabit.logTitle', { title })}
          </DialogTitle>
        )}
        <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
          {t(best ? 'numberHabit.bestToday' : 'numberHabit.todaySoFar', {
            total: fmt(total, lang),
            target: fmt(measure.target, lang),
            unit: measure.unit,
          })}
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
        className="flex flex-col gap-3"
      >
        <label className="flex items-baseline justify-center gap-2 rounded-2xl border border-border/60 bg-card/40 px-4 py-3">
          <input
            autoFocus={!isMobile}
            inputMode="decimal"
            enterKeyHint="done"
            aria-label={t('numberHabit.amount')}
            placeholder="0"
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/[^\d.,]/g, '').slice(0, 9))}
            className="w-0 min-w-0 flex-1 bg-transparent text-right font-mono text-4xl font-black tabular-nums text-foreground outline-none placeholder:text-muted-foreground/40"
          />
          <span className="flex-1 truncate font-mono text-base font-semibold text-muted-foreground">
            {measure.unit}
          </span>
        </label>
        <div className="flex flex-wrap justify-center gap-1.5">
          {measure.quickAdds.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setValue(String(best ? q : roundAmount((valid ? amount : 0) + q)))}
              className="cursor-pointer rounded-full border border-border/60 bg-card/40 px-3 py-1.5 font-mono text-xs font-semibold tabular-nums text-foreground transition-colors hover:border-primary/50 hover:bg-primary/10"
            >
              {best ? '' : '+'}
              {fmt(q, lang)}
            </button>
          ))}
        </div>
        <p
          className={cn(
            'text-center font-mono text-[11px]',
            line.tone === 'over' ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground',
          )}
        >
          {t(best ? 'numberHabit.bestAfterThis' : 'numberHabit.afterThis', {
            total: fmt(after, lang),
            target: fmt(measure.target, lang),
            rest: line.text,
          })}
        </p>
        <input
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('numberHabit.notePlaceholder')}
          className="h-9 rounded-lg border border-border/60 bg-transparent px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-primary/50"
        />
        <button
          type="submit"
          disabled={!valid}
          className="flex h-11 w-full cursor-pointer items-center justify-center rounded-xl bg-primary text-sm font-extrabold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {valid
            ? t(best ? 'numberHabit.logEntry' : 'numberHabit.addAmount', {
                amount: fmt(amount, lang),
                unit: measure.unit,
              })
            : t(best ? 'numberHabit.logSet' : 'numberHabit.enterAmount')}
        </button>
      </form>
      <div>
        <p className="mb-1.5 text-xs font-semibold text-foreground">{t('numberHabit.today')}</p>
        {!day?.entries.length ? (
          <p className="text-xs text-muted-foreground">{t('numberHabit.noEntries')}</p>
        ) : (
          <ul
            // A short list stays part of the sheet, so a swipe on it dismisses.
            // A long one scrolls on its own and opts back into vertical panning.
            {...(day.entries.length > 4 ? { 'data-sheet-scroll': '' } : {})}
            className={cn(
              'divide-y divide-border/60 rounded-xl border border-border/60',
              day.entries.length > 4 && 'max-h-48 overflow-y-auto overscroll-contain',
            )}
          >
            {day.entries.map((e) => (
              <li key={e._id} className="flex items-center gap-2 py-1 pr-1 pl-3">
                <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
                  {fmt(e.amount, lang)}{' '}
                  <span className="text-muted-foreground">{measure.unit}</span>
                </span>
                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                  {e.note}
                </span>
                <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                  {new Date(e.createdAt).toLocaleTimeString(lang, {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
                <button
                  type="button"
                  aria-label={t('numberHabit.deleteEntry')}
                  onClick={() =>
                    void deleteEntry({ entryId: e._id }).catch(() =>
                      toast.error(t('numberHabit.logError')),
                    )
                  }
                  className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <HabitDrawer open={open} onOpenChange={onOpenChange}>
        {body}
      </HabitDrawer>
    )
  }
  return (
    <div className="contents" {...rowQuiet}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[85dvh] overflow-y-auto">{body}</DialogContent>
      </Dialog>
    </div>
  )
}

// ─── fasting ─────────────────────────────────────────────────────────────────

// "14 h 32 m"; whole minutes, rounded down so a fast never reads longer than it was.
export function fastDuration(t: TFunction, hours: number) {
  const mins = Math.max(0, Math.floor(hours * 60 + 1e-6))
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h && m ? t('fasting.hm', { h, m }) : h ? t('fasting.h', { h }) : t('fasting.m', { m })
}

// Ticks once a minute (or every `every` ms) while active, and on focus so a backgrounded tab catches up.
export function useNow(active: boolean, every = 60_000) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const tick = () => setNow(Date.now())
    tick()
    const id = setInterval(tick, every)
    window.addEventListener('focus', tick)
    document.addEventListener('visibilitychange', tick)
    return () => {
      clearInterval(id)
      window.removeEventListener('focus', tick)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [active, every])
  return now
}

type FastActions = ReturnType<typeof useFastActions>
type Ended = { entryId: Id<'habitLogs'>; hours: number; done: boolean; retired: boolean }

const endedPatch = (h: {
  fast?: { startedAt: number } | null
  measureToday?: { date: string; total: number; count: number } | null
}) => {
  if (!h.fast) return {}
  const hours = roundAmount((Date.now() - h.fast.startedAt) / HOUR_MS)
  return {
    fast: null,
    measureToday: {
      date: h.measureToday?.date ?? '',
      total: Math.max(h.measureToday?.total ?? 0, hours),
      count: (h.measureToday?.count ?? 0) + 1,
    },
  }
}

// Start / end / toggle a fast from anywhere (rows, swipes, check circles), each
// with an Undo toast: undoing a start cancels it, undoing an end resumes the fast.
export function useFastActions() {
  const { t } = useTranslation()
  const habits = useQuery(api.nodes.getHabits, {})
  const startM = useMutation(api.fasting.startFast).withOptimisticUpdate(
    (store, { habitId, startedAt }) =>
      patchHabit(store, habitId, (h) =>
        h.fast ? {} : { fast: { startedAt: startedAt ?? Date.now() } },
      ),
  )
  const setStartM = useMutation(api.fasting.setFastStart).withOptimisticUpdate(
    (store, { habitId, startedAt }) => patchHabit(store, habitId, { fast: { startedAt } }),
  )
  const cancelM = useMutation(api.fasting.cancelFast).withOptimisticUpdate((store, { habitId }) =>
    patchHabit(store, habitId, { fast: null }),
  )
  const endM = useMutation(api.fasting.endFast).withOptimisticUpdate((store, { habitId }) =>
    patchHabit(store, habitId, endedPatch),
  )
  const toggleM = useMutation(api.fasting.toggleFast).withOptimisticUpdate((store, { habitId }) =>
    patchHabit(store, habitId, (h) =>
      h.fast ? endedPatch(h) : { fast: { startedAt: Date.now() } },
    ),
  )
  const resumeM = useMutation(api.fasting.resumeFast)
  const fail = () => toast.error(t('fasting.error'))
  const tzOffset = () => new Date().getTimezoneOffset()
  const habitOf = (habitId: string) => habits?.find((h) => h._id === habitId)

  const onStarted = (habitId: Id<'nodes'>) =>
    undoToast(t('fasting.started'), () => void cancelM({ habitId }).catch(fail))
  const onEnded = (habitId: Id<'nodes'>, r: Ended, wasMet: boolean) => {
    if (r.done && !wasMet) {
      haptic('success')
      playDoneSound()
    }
    const duration = fastDuration(t, r.hours)
    undoToast(
      t(r.retired ? 'fasting.endedOnce' : 'fasting.ended', { duration }),
      () => void resumeM({ habitId, entryId: r.entryId }).catch(fail),
    )
  }
  const wasMetToday = (habitId: string) => {
    const h = habitOf(habitId)
    return (
      !!h?.measure &&
      isMeasureMet(h.measure, h.measureToday?.total ?? 0, h.measureToday?.count ?? 0)
    )
  }

  return {
    isFastHabit: (habitId: string) => !!habitOf(habitId)?.measure?.fast,
    start: async (habitId: Id<'nodes'>) => {
      haptic('light')
      try {
        await startM({ habitId, tzOffset: tzOffset() })
        onStarted(habitId)
      } catch {
        fail()
      }
    },
    setStart: (habitId: Id<'nodes'>, startedAt: number) =>
      setStartM({ habitId, startedAt }).catch(fail),
    cancel: (habitId: Id<'nodes'>) => cancelM({ habitId }).catch(fail),
    end: async (habitId: Id<'nodes'>, p: Pick<MeasureProgress, 'measure' | 'total' | 'count'>) => {
      const wasMet = isMeasureMet(p.measure, p.total, p.count)
      haptic('light')
      try {
        const r = await endM({ habitId, tzOffset: tzOffset() })
        if (r) onEnded(habitId, r, wasMet)
        else toast(t('fasting.tooShort'))
        return r
      } catch {
        fail()
        return null
      }
    },
    // One tap / swipe: start when idle, end when running.
    toggle: async (habitId: Id<'nodes'>) => {
      const wasMet = wasMetToday(habitId)
      haptic('light')
      try {
        const r = await toggleM({ habitId, tzOffset: tzOffset() })
        if (r.kind === 'started') onStarted(habitId)
        else if (r.kind === 'ended') onEnded(habitId, r, wasMet)
        else toast(t('fasting.tooShort'))
        return r
      } catch {
        fail()
        return null
      }
    },
  }
}

function FastProgress({
  progress: p,
  now,
  className,
}: {
  progress: MeasureProgress
  now: number
  className?: string
}) {
  const { t } = useTranslation()
  const target = p.measure.target
  const elapsed = p.fast ? Math.max(0, now - p.fast.startedAt) / HOUR_MS : p.total
  const reached = elapsed >= target
  const line = p.fast
    ? reached
      ? t('fasting.reached', { over: fastDuration(t, elapsed - target) })
      : t('fasting.toGo', { left: fastDuration(t, target - elapsed) })
    : reached
      ? t('numberHabit.doneHit')
      : t('fasting.idle')
  return (
    <div className={cn('min-w-0', className)}>
      <p className="font-mono text-[13px] font-semibold leading-none tabular-nums text-foreground">
        {fastDuration(t, elapsed)}
        <span className="font-medium text-muted-foreground"> / {fastDuration(t, target)}</span>
      </p>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-foreground/[0.08]" aria-hidden>
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-300',
            reached
              ? 'bg-[var(--habit-accent,var(--primary))]'
              : 'bg-[color-mix(in_srgb,var(--habit-accent,var(--primary))_70%,transparent)]',
          )}
          style={{ width: `${Math.min(100, (elapsed / target) * 100)}%` }}
        />
      </div>
      <p
        className={cn(
          'mt-1 font-mono text-[11px] leading-none',
          reached ? 'text-[var(--habit-accent,var(--primary-text))]' : 'text-muted-foreground',
        )}
      >
        {line}
      </p>
    </div>
  )
}

const stopRow = {
  onPointerDown: (e: { stopPropagation: () => void }) => e.stopPropagation(),
  onTouchStart: (e: { stopPropagation: () => void }) => e.stopPropagation(),
}

function FastHabitRow({
  habitId,
  progress: p,
  actions,
  className,
}: {
  habitId: Id<'nodes'>
  progress: MeasureProgress
  actions: FastActions
  className?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const now = useNow(!!p.fast)
  const accent = useHabitAccent(habitId)
  return (
    <div
      className={cn('flex items-center gap-3', className)}
      style={accent ? ({ '--habit-accent': accent } as CSSProperties) : undefined}
    >
      {p.fast ? (
        <button
          type="button"
          {...stopRow}
          onClick={(e) => {
            e.stopPropagation()
            setOpen(true)
          }}
          aria-label={t('fasting.editAria', { title: p.title })}
          className="min-w-0 flex-1 cursor-pointer rounded-md text-left"
        >
          <FastProgress progress={p} now={now} />
        </button>
      ) : (
        <FastProgress progress={p} now={now} className="flex-1" />
      )}
      <button
        type="button"
        {...stopRow}
        onClick={(e) => {
          e.stopPropagation()
          void (p.fast ? actions.end(habitId, p) : actions.start(habitId))
        }}
        className={cn(
          'flex h-11 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-3.5 text-xs font-bold transition-colors active:scale-95',
          p.fast
            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
            : 'border-primary/30 bg-primary/10 text-primary-text hover:bg-primary/20',
        )}
      >
        {p.fast ? (
          <Square className="size-3.5" strokeWidth={2.5} />
        ) : (
          <Play className="size-3.5" strokeWidth={2.5} />
        )}
        {t(p.fast ? 'fasting.end' : 'fasting.start')}
      </button>
      {p.fast && (
        <FastSheet
          open={open}
          onOpenChange={setOpen}
          habitId={habitId}
          progress={p}
          now={now}
          actions={actions}
        />
      )}
    </div>
  )
}

// ─── timer (minutes habits) ──────────────────────────────────────────────────

type TimerActions = ReturnType<typeof useTimerActions>

// Start / stop a minutes habit's timer. Stopping logs the minutes (Undo resumes it).
export function useTimerActions() {
  const { t, i18n } = useTranslation()
  const habits = useQuery(api.nodes.getHabits, {})
  const startM = useMutation(api.habitTimer.startTimer).withOptimisticUpdate(
    (store, { habitId, minutes }) =>
      patchHabit(store, habitId, { timer: { startedAt: Date.now(), minutes: minutes ?? null } }),
  )
  const stopM = useMutation(api.habitTimer.stopTimer).withOptimisticUpdate((store, { habitId }) =>
    patchHabit(store, habitId, { timer: null }),
  )
  const cancelM = useMutation(api.habitTimer.cancelTimer).withOptimisticUpdate(
    (store, { habitId }) => patchHabit(store, habitId, { timer: null }),
  )
  const resumeM = useMutation(api.habitTimer.resumeTimer)
  const fail = () => toast.error(t('habitTimer.error'))
  const tzOffset = () => new Date().getTimezoneOffset()

  return {
    start: async (habitId: Id<'nodes'>, minutes?: number) => {
      haptic('light')
      try {
        await startM({ habitId, tzOffset: tzOffset(), ...(minutes && { minutes }) })
      } catch {
        fail()
      }
    },
    stop: async (habitId: Id<'nodes'>, p: Pick<MeasureProgress, 'measure' | 'total' | 'count'>) => {
      const wasMet = isMeasureMet(p.measure, p.total, p.count)
      haptic('light')
      try {
        const r = await stopM({ habitId, tzOffset: tzOffset() })
        if (r.kind === 'logged') {
          if (r.done && !wasMet) {
            haptic('success')
            playDoneSound()
          }
          undoToast(
            t('habitTimer.logged', { minutes: fmt(r.minutes, i18n.language) }),
            () => void resumeM({ habitId, entryId: r.entryId }).catch(fail),
          )
        } else if (r.kind === 'discarded') toast(t('habitTimer.tooShort'))
        return r
      } catch {
        fail()
        return null
      }
    },
    cancel: (habitId: Id<'nodes'>) => cancelM({ habitId }).catch(fail),
    running: (habitId: string) => !!habits?.find((h) => h._id === habitId)?.timer,
  }
}

// A soft bell when a countdown reaches its end while the app is open (the server
// logs the minutes either way). Mounted once at the app root.
export function TimerBell() {
  const { t } = useTranslation()
  const habits = useQuery(api.nodes.getHabits, {})
  const runs = (habits ?? []).flatMap((h) =>
    h.timer?.minutes
      ? [
          {
            key: `${h._id}:${h.timer.startedAt}`,
            at: h.timer.startedAt + h.timer.minutes * 60_000,
            minutes: h.timer.minutes,
            title: parseTaskFields(h.title).title,
          },
        ]
      : [],
  )
  const sig = runs.map((r) => r.key).join()
  // biome-ignore lint/correctness/useExhaustiveDependencies: `sig` is the identity of `runs`
  useEffect(() => {
    const ids = runs.flatMap((r) => {
      const wait = r.at - Date.now()
      if (wait < -5000) return []
      return [
        setTimeout(
          () => {
            playBellSound()
            toast.success(t('habitTimer.done', { title: r.title, minutes: r.minutes }))
          },
          Math.max(0, wait),
        ),
      ]
    })
    return () => ids.forEach(clearTimeout)
  }, [sig])
  return null
}

// h:mm:ss under a day, mm:ss under an hour.
export const clock = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000 - 1e-6))
  const h = Math.floor(s / 3600)
  const mm = String(Math.floor((s % 3600) / 60)).padStart(h ? 2 : 1, '0')
  const ss = String(s % 60).padStart(2, '0')
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

const timerRatio = (p: MeasureProgress, now: number) => {
  const tm = p.timer
  if (!tm) return 0
  const elapsed = Math.max(0, now - tm.startedAt)
  return tm.minutes
    ? elapsed / (tm.minutes * 60_000)
    : (p.total + elapsed / 60_000) / p.measure.target
}

const RING_R = 15
const RING_C = 2 * Math.PI * RING_R

export function TimerRing({ ratio }: { ratio: number }) {
  return (
    <span className="relative flex size-10 shrink-0 items-center justify-center" aria-hidden>
      <svg viewBox="0 0 36 36" className="absolute inset-0 -rotate-90">
        <circle
          cx="18"
          cy="18"
          r={RING_R}
          fill="none"
          strokeWidth="3"
          className="stroke-foreground/[0.08]"
        />
        <circle
          cx="18"
          cy="18"
          r={RING_R}
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={RING_C}
          strokeDashoffset={RING_C * (1 - Math.min(1, Math.max(0, ratio)))}
          className="stroke-[var(--habit-accent,var(--primary))] transition-[stroke-dashoffset] duration-500 motion-reduce:transition-none"
        />
      </svg>
      <Timer className="size-4 text-muted-foreground" strokeWidth={2.2} />
    </span>
  )
}

function TimerProgress({
  progress: p,
  now,
  className,
}: {
  progress: MeasureProgress
  now: number
  className?: string
}) {
  const { t, i18n } = useTranslation()
  const tm = p.timer
  if (!tm) return null
  const elapsed = Math.max(0, now - tm.startedAt)
  const planned = tm.minutes ? tm.minutes * 60_000 : null
  const ratio = timerRatio(p, now)
  return (
    <div className={cn('min-w-0', className)} role="timer">
      <p className="font-mono text-[15px] font-semibold leading-none tabular-nums text-foreground">
        {clock(planned ? planned - elapsed : elapsed)}
        {planned && (
          <span className="text-[11px] font-medium text-muted-foreground">
            {' '}
            / {fmt(tm.minutes ?? 0, i18n.language)} {p.measure.unit}
          </span>
        )}
      </p>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-foreground/[0.08]" aria-hidden>
        <div
          className="h-full rounded-full bg-[var(--habit-accent,var(--primary))] transition-[width] duration-500"
          style={{ width: `${Math.min(100, ratio * 100)}%` }}
        />
      </div>
      <p className="mt-1 font-mono text-[11px] leading-none text-muted-foreground">
        {planned
          ? t('habitTimer.leftLine', { time: clock(planned - elapsed) })
          : t('habitTimer.openLine', {
              total: fmt(p.measure.target, i18n.language),
              unit: p.measure.unit,
            })}
      </p>
    </div>
  )
}

function TimerSection({
  habitId,
  progress: p,
  actions,
  onLog,
}: {
  habitId: Id<'nodes'>
  progress: MeasureProgress
  actions: TimerActions
  onLog: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [pacer, setPacer] = useState(false)
  const breath = breathPatternFor(p.title)
  const accent = useHabitAccent(habitId)
  const now = useNow(!!p.timer, 1000)
  useEffect(() => {
    if (!p.timer) setPacer(false)
  }, [p.timer])
  useEffect(() => {
    if (!confirmCancel) return
    const id = setTimeout(() => setConfirmCancel(false), 3000)
    return () => clearTimeout(id)
  }, [confirmCancel])
  if (!p.timer)
    return (
      <>
        <NumberHabitProgress
          measure={p.measure}
          total={p.total}
          count={p.count}
          className="flex-1"
        />
        <button
          type="button"
          {...stopRow}
          onClick={(e) => {
            e.stopPropagation()
            setOpen(true)
          }}
          aria-label={t('habitTimer.startAria', { title: p.title })}
          className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary-text transition-colors hover:bg-primary/20 active:scale-95"
        >
          <Play className="size-4" strokeWidth={2.5} />
        </button>
        <AddButton
          measure={p.measure}
          title={p.title}
          met={isMeasureMet(p.measure, p.total, p.count)}
          onClick={onLog}
        />
        <TimerStartSheet
          open={open}
          onOpenChange={setOpen}
          habitId={habitId}
          progress={p}
          actions={actions}
          onStarted={() => breath && !pacerSkipped(habitId) && setPacer(true)}
        />
      </>
    )
  return (
    <>
      <TimerRing ratio={timerRatio(p, now)} />
      <TimerProgress progress={p} now={now} className="flex-1" />
      {breath && (
        <button
          type="button"
          {...stopRow}
          onClick={(e) => {
            e.stopPropagation()
            setPacerSkipped(habitId, false)
            setPacer(true)
          }}
          aria-label={t('breathPacer.guidedAria', { title: p.title })}
          title={t('breathPacer.guided')}
          className="flex h-11 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-full px-2.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Wind className="size-4" />
          <span className="hidden sm:inline">{t('breathPacer.guided')}</span>
        </button>
      )}
      {breath && pacer && p.timer && (
        <BreathPacer
          habitId={habitId}
          title={p.title}
          initialPattern={breath}
          startedAt={p.timer.startedAt}
          targetMs={
            p.timer.minutes
              ? p.timer.minutes * 60_000
              : Math.max(1, p.measure.target - p.total) * 60_000
          }
          accent={accent}
          onMinimize={() => setPacer(false)}
          onJustTimer={() => {
            setPacerSkipped(habitId, true)
            setPacer(false)
          }}
          onEnd={() => void actions.stop(habitId, p)}
          onTarget={() => void actions.stop(habitId, p)}
        />
      )}
      <button
        type="button"
        {...stopRow}
        onClick={(e) => {
          e.stopPropagation()
          if (!confirmCancel) return setConfirmCancel(true)
          setConfirmCancel(false)
          void actions.cancel(habitId)
        }}
        aria-label={t(confirmCancel ? 'habitTimer.cancelConfirm' : 'habitTimer.cancel')}
        className={cn(
          'flex h-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-xs font-semibold transition-colors',
          confirmCancel
            ? 'bg-destructive/10 px-3 text-destructive'
            : 'w-9 text-muted-foreground hover:bg-muted',
        )}
      >
        {confirmCancel ? t('habitTimer.cancelConfirm') : <X className="size-4" />}
      </button>
      <button
        type="button"
        {...stopRow}
        onClick={(e) => {
          e.stopPropagation()
          void actions.stop(habitId, p)
        }}
        className="flex h-11 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-primary bg-primary px-3.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90 active:scale-95"
      >
        <Square className="size-3.5" strokeWidth={2.5} />
        {t('habitTimer.stop')}
      </button>
    </>
  )
}

function TimerStartSheet({
  open,
  onOpenChange,
  habitId,
  progress: p,
  actions,
  onStarted,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  habitId: Id<'nodes'>
  progress: MeasureProgress
  actions: TimerActions
  onStarted?: () => void
}) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobile()
  const [value, setValue] = useState('')
  const Title = isMobile ? DrawerTitle : DialogTitle

  useEffect(() => {
    if (!open) return
    const left = isBestOfDay(p.measure) ? p.measure.target : p.measure.target - p.total
    setValue(String(Math.max(1, Math.ceil(left - 1e-6))))
    // Only reset when opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const minutes = Math.round(Number(value))
  const valid = Number.isFinite(minutes) && minutes >= 1 && minutes <= MAX_TIMER_MINUTES
  const go = (m?: number) => {
    onOpenChange(false)
    void actions.start(habitId, m)
    onStarted?.()
  }

  const body = (
    <div className="flex flex-col gap-4">
      <div className="pr-8">
        <Title className="text-base font-extrabold">
          {t('habitTimer.sheetTitle', { title: p.title })}
        </Title>
        <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
          {t('numberHabit.todaySoFar', {
            total: fmt(p.total, i18n.language),
            target: fmt(p.measure.target, i18n.language),
            unit: p.measure.unit,
          })}
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (valid) go(minutes)
        }}
        className="flex flex-col gap-3"
      >
        <label className="flex items-baseline justify-center gap-2 rounded-2xl border border-border/60 bg-card/40 px-4 py-3">
          <input
            autoFocus={!isMobile}
            inputMode="numeric"
            enterKeyHint="go"
            aria-label={t('habitTimer.minutes')}
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 3))}
            className="w-0 min-w-0 flex-1 bg-transparent text-right font-mono text-4xl font-black tabular-nums text-foreground outline-none"
          />
          <span className="flex-1 truncate font-mono text-base font-semibold text-muted-foreground">
            {t('habitTimer.minutes')}
          </span>
        </label>
        <button
          type="submit"
          disabled={!valid}
          className="flex h-11 w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Play className="size-3.5" strokeWidth={2.5} />
          {t('habitTimer.startCountdown', { minutes: valid ? minutes : '…' })}
        </button>
      </form>
      <button
        type="button"
        onClick={() => go()}
        className="flex h-11 w-full cursor-pointer flex-col items-center justify-center rounded-xl border border-border/60 text-sm font-bold text-foreground transition-colors hover:bg-muted"
      >
        {t('habitTimer.openEnded')}
        <span className="text-[11px] font-medium text-muted-foreground">
          {t('habitTimer.openEndedHint')}
        </span>
      </button>
    </div>
  )

  if (isMobile) {
    return (
      <HabitDrawer open={open} onOpenChange={onOpenChange}>
        {body}
      </HabitDrawer>
    )
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto" onClick={stop}>
        {body}
      </DialogContent>
    </Dialog>
  )
}

const toLocalInput = (ms: number) =>
  new Date(ms - new Date(ms).getTimezoneOffset() * 60_000).toISOString().slice(0, 16)

function FastSheet({
  open,
  onOpenChange,
  habitId,
  progress: p,
  now,
  actions,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  habitId: Id<'nodes'>
  progress: MeasureProgress
  now: number
  actions: FastActions
}) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const startedAt = p.fast?.startedAt ?? now
  const [value, setValue] = useState('')
  const [confirmCancel, setConfirmCancel] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.isComposing) return
      e.stopPropagation()
      onOpenChange(false)
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open) return
    setValue(toLocalInput(startedAt))
    setConfirmCancel(false)
  }, [open, startedAt])

  const picked = value ? new Date(value).getTime() : NaN
  const earliest = Date.now() - MAX_FAST_HOURS * HOUR_MS
  const valid = Number.isFinite(picked) && picked <= Date.now() && picked >= earliest
  const changed = valid && Math.abs(picked - startedAt) >= 60_000
  const Title = isMobile ? DrawerTitle : DialogTitle

  const body = (
    <div className="flex flex-col gap-4">
      <div className="pr-8">
        <Title className="text-base font-extrabold">{p.title}</Title>
        <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
          {t('fasting.sinceLine', {
            time: new Date(startedAt).toLocaleString(undefined, {
              weekday: 'short',
              hour: 'numeric',
              minute: '2-digit',
            }),
          })}
        </p>
      </div>
      <FastProgress progress={p} now={now} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!changed) return
          void actions.setStart(habitId, picked)
          onOpenChange(false)
        }}
        className="flex flex-col gap-2"
      >
        <label className="text-xs font-semibold text-foreground" htmlFor="fast-start">
          {t('fasting.startedAt')}
        </label>
        <input
          id="fast-start"
          type="datetime-local"
          value={value}
          max={toLocalInput(Date.now())}
          min={toLocalInput(earliest)}
          onChange={(e) => setValue(e.target.value)}
          className="h-10 rounded-lg border border-border/60 bg-transparent px-3 font-mono text-sm text-foreground outline-none focus:border-primary/50"
        />
        <button
          type="submit"
          disabled={!changed}
          className="h-10 cursor-pointer rounded-xl border border-border/60 text-sm font-bold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('fasting.saveStart')}
        </button>
      </form>
      <button
        type="button"
        onClick={() => {
          onOpenChange(false)
          void actions.end(habitId, p)
        }}
        className="flex h-11 w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        <Square className="size-3.5" strokeWidth={2.5} />
        {t('fasting.end')}
      </button>
      <button
        type="button"
        onClick={() => {
          if (!confirmCancel) return setConfirmCancel(true)
          onOpenChange(false)
          void actions.cancel(habitId)
        }}
        className="h-9 cursor-pointer rounded-lg text-xs font-semibold text-destructive transition-colors hover:bg-destructive/10"
      >
        {t(confirmCancel ? 'fasting.cancelConfirm' : 'fasting.cancel')}
      </button>
    </div>
  )

  if (isMobile) {
    return (
      <HabitDrawer open={open} onOpenChange={onOpenChange}>
        {body}
      </HabitDrawer>
    )
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto" onClick={stop}>
        {body}
      </DialogContent>
    </Dialog>
  )
}

// ─── create / edit ───────────────────────────────────────────────────────────

export type MeasurePreset = {
  id: MeasurePresetId | FastPresetId
  measure: HabitMeasure
  title: string
  desc: string
  icon: string
  category: LifeCategory
  // Suggested schedule; multi-day fasts don't fit a daily one.
  period?: 'daily' | 'weekly' | 'monthly'
}

export function presetMeasure(t: TFunction, id: MeasurePresetId): MeasurePreset {
  const p = MEASURE_PRESETS.find((x) => x.id === id)!
  return {
    id,
    measure: { ...p.measure, unit: t(`numberHabit.presets.${id}.unit`) },
    title: t(`numberHabit.presets.${id}.title`),
    desc: t(`numberHabit.presets.${id}.desc`),
    icon: p.icon,
    category: p.category,
  }
}

export function fastPreset(t: TFunction, id: FastPresetId): MeasurePreset {
  const f = FAST_PRESETS.find((x) => x.id === id)!
  return {
    id,
    measure: fastMeasure(f.hours, t('fasting.unit')),
    title: t(`fasting.presets.${id}.title`),
    desc: t(`fasting.presets.${id}.desc`),
    icon: 'hourglass',
    category: 'health',
    period: f.period,
  }
}

const DEFAULT_MEASURE: HabitMeasure = {
  direction: 'atLeast',
  target: 10,
  unit: '',
  quickAdds: [1],
}

function QuickAddsEditor({
  value,
  onChange,
  plain,
  className,
}: {
  value: number[]
  onChange: (quickAdds: number[]) => void
  plain?: boolean
  className?: string
}) {
  const { t, i18n } = useTranslation()
  const [chip, setChip] = useState('')
  const lang = i18n.language
  const sign = plain ? '' : '+'
  const addChip = () => {
    const n = Number(chip.replace(',', '.'))
    if (!isValidAmount(n) || value.length >= MAX_QUICK_ADDS) return
    onChange([...new Set([...value, roundAmount(n)])].sort((a, b) => a - b))
    setChip('')
  }
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {value.map((q) => (
        <span
          key={q}
          className="flex items-center gap-0.5 rounded-full bg-primary/10 py-0.5 pr-1 pl-2.5 font-mono text-xs font-semibold tabular-nums text-primary-text"
        >
          {sign}
          {fmt(q, lang)}
          <button
            type="button"
            aria-label={t('numberHabit.removeQuickAdd', { amount: `${sign}${fmt(q, lang)}` })}
            onClick={() => onChange(value.filter((x) => x !== q))}
            className="flex size-5 cursor-pointer items-center justify-center rounded-full hover:bg-primary/20"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      {value.length < MAX_QUICK_ADDS && (
        <span className="flex items-center gap-1">
          <input
            inputMode="decimal"
            value={chip}
            placeholder={sign || '0'}
            aria-label={t('numberHabit.addQuickAdd')}
            onChange={(e) => setChip(e.target.value.replace(/[^\d.,]/g, '').slice(0, 7))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addChip()
              }
            }}
            className="h-7 w-16 rounded-full border border-dashed border-border bg-transparent px-2.5 font-mono text-xs text-foreground outline-none focus:border-primary/50"
          />
          {chip && (
            <button
              type="button"
              onClick={addChip}
              className="cursor-pointer text-xs font-semibold text-primary-text"
            >
              {t('numberHabit.addQuickAdd')}
            </button>
          )}
        </span>
      )}
    </div>
  )
}

export const isMeasureReady = (m: HabitMeasure | null) => !m || isValidAmount(m.target)

// Short chip label for a measure: "≤ 2,000 kcal".
export function measureChipLabel(m: HabitMeasure, lang: string) {
  return `${m.direction === 'atMost' ? '≤' : '≥'} ${fmt(m.target, lang)} ${m.unit}`.trim()
}

const eyebrow = 'text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70'
const pill = (on: boolean) =>
  cn(
    'min-w-0 flex-1 cursor-pointer truncate rounded-full border px-2 py-1 text-[11px] transition-colors',
    on
      ? 'border-primary/50 bg-primary/10 font-bold text-primary-text'
      : 'border-border text-foreground hover:bg-muted',
  )
const field =
  'min-w-0 rounded-md border border-border bg-background px-1.5 py-1 text-foreground outline-none focus:border-primary/60'

// Shared body of the composer `#` popover and the detail sheet's Tracking
// section: presets, goal, count mode, custom target/unit and quick adds.
export type FastSchedule = 'once' | 'weekly' | 'monthly'
const FAST_SCHEDULES: FastSchedule[] = ['once', 'weekly', 'monthly']

function MeasureForm({
  draft,
  onChange,
  picked,
  onPick,
  schedule,
  onSchedule,
}: {
  draft: HabitMeasure
  onChange: (m: HabitMeasure) => void
  picked: MeasurePreset | null
  onPick: (preset: MeasurePreset) => void
  // Multi-day fasts: run once, or repeat weekly / monthly.
  schedule?: FastSchedule
  onSchedule?: (s: FastSchedule) => void
}) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const set = (patch: Partial<HabitMeasure>) => onChange({ ...draft, ...patch })
  const best = isBestOfDay(draft)
  const fasting = isFast(draft)
  const [unitSwitched, setUnitSwitched] = useState(false)
  const timerOn = isTimerMeasure(draft)
  const toggleTimer = (on: boolean) => {
    const wasMinutes = isTimerMeasure({ ...draft, timer: undefined })
    const unit = on && !wasMinutes ? t('numberHabit.presets.meditate.unit') : draft.unit
    setUnitSwitched(on && unit !== draft.unit)
    onChange({ ...draft, unit, timer: on })
  }
  return (
    <div className="flex min-w-0 flex-col">
      <p className="mb-2 text-[11px] leading-snug text-muted-foreground">
        {t('numberHabit.explainer')}
      </p>
      <div className="mb-3 grid grid-cols-3 gap-1.5">
        {MEASURE_PRESETS.map((p) => {
          const preset = presetMeasure(t, p.id)
          const Icon = habitIconComponent(p.icon)
          const on = picked?.id === p.id
          return (
            <button
              key={p.id}
              type="button"
              title={preset.desc}
              aria-pressed={on}
              onClick={() => onPick(preset)}
              className={cn(
                'flex min-w-0 cursor-pointer flex-col items-center rounded-lg border px-1 py-1.5 text-center text-[11px] font-semibold text-foreground transition-colors',
                on ? 'border-primary/50 bg-primary/10' : 'border-border hover:bg-muted',
              )}
            >
              <Icon
                className={cn(
                  'mb-0.5 size-3.5',
                  on ? 'text-primary-text' : 'text-muted-foreground',
                )}
                strokeWidth={2.2}
              />
              <span className="w-full truncate">{preset.title}</span>
              <span className="mt-0.5 block w-full truncate font-mono text-[10px] font-medium text-muted-foreground">
                {measureChipLabel(preset.measure, lang)}
              </span>
            </button>
          )
        })}
      </div>
      <p className={cn(eyebrow, 'mb-2')}>{t('fasting.label')}</p>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {FAST_PRESETS.map((f) => {
          const preset = fastPreset(t, f.id)
          const on = picked?.id === f.id
          return (
            <button
              key={f.id}
              type="button"
              title={preset.desc}
              aria-pressed={on}
              onClick={() => onPick(preset)}
              className={cn(
                'cursor-pointer rounded-full border px-2.5 py-1 font-mono text-[11px] font-semibold transition-colors',
                on
                  ? 'border-primary/50 bg-primary/10 text-primary-text'
                  : 'border-border text-foreground hover:bg-muted',
              )}
            >
              {t(`fasting.presets.${f.id}.chip`)}
            </button>
          )
        })}
      </div>
      {fasting ? (
        <>
          <p className="mb-2 text-[11px] text-muted-foreground">{t('fasting.hint')}</p>
          <div className="flex min-w-0 items-center gap-2 text-xs">
            <span className="shrink-0 text-[11px] text-muted-foreground">
              {t('fasting.targetHours')}
            </span>
            <input
              inputMode="decimal"
              aria-label={t('fasting.targetHours')}
              value={draft.target ? String(draft.target) : ''}
              onChange={(e) => {
                const n = Number(e.target.value.replace(',', '.'))
                if (e.target.value === '' || (Number.isFinite(n) && n <= MAX_FAST_HOURS))
                  set({ target: n || 0, quickAdds: n ? [n] : draft.quickAdds })
              }}
              className={cn(field, 'w-20 font-mono tabular-nums')}
            />
            <span className="text-[11px] text-muted-foreground">{draft.unit}</span>
          </div>
          {onSchedule && isMultiDayFast(draft) && (
            <div className="mt-2 flex gap-1.5" role="group" aria-label={t('fasting.scheduleLabel')}>
              {FAST_SCHEDULES.map((f) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={schedule === f}
                  onClick={() => onSchedule(f)}
                  className={pill(schedule === f)}
                >
                  {t(`fasting.schedule.${f}`)}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <p className={cn(eyebrow, 'mb-2')}>{t('numberHabit.goal')}</p>
          {!best && (
            <div className="flex gap-1.5">
              {(['atLeast', 'atMost'] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => set({ direction: d })}
                  className={pill(draft.direction === d)}
                >
                  {t(`numberHabit.direction.${d}`)}
                </button>
              ))}
            </div>
          )}
          <p className={cn('mb-3 text-[11px] text-muted-foreground', !best && 'mt-1')}>
            {t(draft.direction === 'atMost' ? 'numberHabit.atMostHint' : 'numberHabit.atLeastHint')}
          </p>
          <p className={cn(eyebrow, 'mb-2')}>{t('numberHabit.count')}</p>
          <div className="flex gap-1.5">
            {(['sum', 'max'] as const).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() =>
                  onChange(
                    a === 'max'
                      ? { ...draft, aggregate: 'max', direction: 'atLeast' }
                      : { ...draft, aggregate: undefined },
                  )
                }
                className={pill((a === 'max') === best)}
              >
                {t(`numberHabit.countMode.${a}`)}
              </button>
            ))}
          </div>
          <p className="mt-1 mb-3 text-[11px] text-muted-foreground">
            {t(`numberHabit.countHint.${best ? 'max' : 'sum'}`)}
          </p>
          <div className="mb-2 flex min-w-0 items-center gap-2 text-xs">
            <span className="shrink-0 text-[11px] text-muted-foreground">
              {t('numberHabit.custom')}
            </span>
            <input
              inputMode="decimal"
              aria-label={t('numberHabit.target')}
              placeholder={t('numberHabit.target')}
              value={draft.target ? String(draft.target) : ''}
              onChange={(e) => {
                const n = Number(e.target.value.replace(',', '.'))
                if (e.target.value === '' || Number.isFinite(n)) set({ target: n || 0 })
              }}
              className={cn(field, 'w-0 flex-[2] font-mono tabular-nums')}
            />
            <input
              aria-label={t('numberHabit.unit')}
              value={draft.unit}
              maxLength={MAX_UNIT_LENGTH}
              placeholder={t('numberHabit.unitPlaceholder')}
              onChange={(e) => set({ unit: e.target.value })}
              className={cn(field, 'w-0 flex-[3] placeholder:text-muted-foreground/50')}
            />
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <span className="shrink-0 text-[11px] text-muted-foreground">
              {t('numberHabit.quickAdds')}
            </span>
            <QuickAddsEditor
              value={draft.quickAdds}
              onChange={(quickAdds) => set({ quickAdds })}
              plain={best}
              className="min-w-0 flex-1"
            />
          </div>
          {draft.direction === 'atLeast' && (
            <>
              <label className="mt-3 flex cursor-pointer items-center justify-between gap-3">
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-foreground">
                    {t('numberHabit.timer.label')}
                  </span>
                  <span className="block text-[11px] text-muted-foreground">
                    {t('numberHabit.timer.desc')}
                  </span>
                </span>
                <input
                  type="checkbox"
                  role="switch"
                  checked={timerOn}
                  onChange={(e) => toggleTimer(e.target.checked)}
                  className="peer sr-only"
                />
                <span
                  aria-hidden
                  className="relative h-6 w-10 shrink-0 rounded-full bg-muted transition-colors peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring/50 after:absolute after:top-[3px] after:left-[3px] after:size-[18px] after:rounded-full after:bg-background after:transition-transform peer-checked:after:translate-x-4"
                />
              </label>
              {unitSwitched && timerOn && (
                <p className="mt-1 text-[11px] text-primary-text">
                  {t('numberHabit.timer.unitHint', { unit: draft.unit })}
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

const saveButton =
  'w-full cursor-pointer truncate rounded-lg bg-primary px-2 py-1.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50'
const kindSwitch =
  'flex w-full cursor-pointer items-center justify-center gap-1.5 border-t border-border pt-2.5 text-xs font-medium transition-colors'

const cleanDraft = (m: HabitMeasure): HabitMeasure => ({ ...m, unit: m.unit.trim() })

// Composer `#`: opening it means "track a number"; the footer turns it back into a check-off.
export function MeasurePopover({
  value,
  onChange,
  onPreset,
  onSchedule,
}: {
  value: HabitMeasure | null
  onChange: (m: HabitMeasure | null) => void
  onPreset?: (preset: MeasurePreset) => void
  // Saved with a multi-day fast: the schedule picked next to it.
  onSchedule?: (s: FastSchedule) => void
}) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<HabitMeasure>(value ?? DEFAULT_MEASURE)
  const [picked, setPicked] = useState<MeasurePreset | null>(null)
  const [schedule, setSchedule] = useState<FastSchedule>('weekly')
  const close = () => setOpen(false)
  const onOpenChange = (next: boolean) => {
    if (next) {
      setDraft(value ?? DEFAULT_MEASURE)
      setPicked(null)
    }
    setOpen(next)
  }
  const triggerProps = {
    onMouseDown: (e: MouseEvent) => e.preventDefault(),
    title: t('numberHabit.chip'),
    'aria-label': t('numberHabit.chip'),
    className: cn(
      'flex h-6 shrink-0 cursor-pointer items-center gap-1 rounded-full transition-colors hover:bg-muted',
      value
        ? 'px-1.5 font-mono text-[11px] font-semibold text-primary-text'
        : 'w-6 justify-center text-muted-foreground/60',
    ),
  }
  const triggerContent = (
    <>
      <Hash className={value ? 'size-2.5' : 'size-3.5'} strokeWidth={2.5} />
      {value && measureChipLabel(value, lang)}
    </>
  )
  const Title = isMobile ? DrawerTitle : 'p'
  const body = (
    <>
      <div className="mb-1 flex items-center justify-between">
        <Title className={eyebrow}>{t('numberHabit.track')}</Title>
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={close}
          className="-my-1 -mr-1 flex size-6 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </div>
      <MeasureForm
        draft={draft}
        onChange={setDraft}
        picked={picked}
        onPick={(preset) => {
          setDraft(preset.measure)
          setPicked(preset)
          if (preset.period === 'weekly' || preset.period === 'monthly') setSchedule(preset.period)
        }}
        schedule={schedule}
        onSchedule={onSchedule && setSchedule}
      />
      <button
        type="button"
        disabled={!isValidAmount(draft.target)}
        onClick={() => {
          const measure = cleanDraft(draft)
          onChange(measure)
          if (picked) onPreset?.({ ...picked, measure })
          if (isMultiDayFast(measure)) onSchedule?.(schedule)
          close()
        }}
        className={cn(saveButton, 'mt-3 mb-3')}
      >
        {t('numberHabit.setMeasure', { label: measureChipLabel(draft, lang) })}
      </button>
      <button
        type="button"
        onClick={() => {
          onChange(null)
          close()
        }}
        className={cn(
          kindSwitch,
          value ? 'text-muted-foreground hover:text-foreground' : 'text-primary-text',
        )}
      >
        {t('numberHabit.checkOffInstead')}
      </button>
    </>
  )

  // A lot of controls for a popover on a phone: use the bottom sheet there.
  if (isMobile)
    return (
      <>
        <button type="button" {...triggerProps} onClick={() => onOpenChange(true)}>
          {triggerContent}
        </button>
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="mx-auto w-full max-w-lg data-[vaul-drawer-direction=bottom]:max-h-[90dvh] data-[vaul-drawer-direction=bottom]:rounded-t-2xl">
            <div className="overflow-y-auto px-5 pt-4 pb-[max(env(safe-area-inset-bottom),20px)] font-sans text-foreground">
              {body}
            </div>
          </DrawerContent>
        </Drawer>
      </>
    )
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger {...triggerProps}>{triggerContent}</PopoverTrigger>
      <PopoverContent className="w-80 gap-0 p-3 font-sans" side="bottom" align="start">
        {body}
      </PopoverContent>
    </Popover>
  )
}

// Habit detail: the same editor as the `#` popover, saved on demand. A
// check-off habit shows a short prompt until "Track a number" expands it.
export function MeasureSettings({ habitId }: { habitId: Id<'nodes'> }): ReactNode {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const habits = useQuery(api.nodes.getHabits)
  const habit = habits?.find((h) => h._id === habitId)
  const saved = habit?.measure ?? null
  const setMeasure = useMutation(api.habitLogs.setMeasure).withOptimisticUpdate(
    (store, { habitId, measure }) => {
      const clean = measure && cleanMeasure(measure)
      if (typeof clean !== 'string') patchHabit(store, habitId, { measure: clean })
    },
  )
  const setFastSchedule = useMutation(api.fasting.setFastSchedule).withOptimisticUpdate(
    (store, { habitId, schedule }) =>
      patchHabit(store, habitId, {
        oneOff: schedule === 'once',
        recursPeriod: schedule === 'once' ? 'daily' : schedule,
      }),
  )
  const [draft, setDraft] = useState<HabitMeasure>(saved ?? DEFAULT_MEASURE)
  const [picked, setPicked] = useState<MeasurePreset | null>(null)
  const [expanded, setExpanded] = useState(false)
  const savedKey = JSON.stringify(saved)
  useEffect(() => {
    setDraft(saved ?? DEFAULT_MEASURE)
    setPicked(null)
  }, [savedKey])
  if (!habit) return null
  const save = (measure: HabitMeasure | null) =>
    void setMeasure({ habitId, measure })
      .then(() => setExpanded(false))
      .catch(() => toast.error(t('numberHabit.saveError')))
  const dirty = !saved || JSON.stringify(cleanDraft(draft)) !== savedKey
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {t('numberHabit.tracking')}
      </span>
      {!saved && !expanded ? (
        <div className="flex items-center gap-3 rounded-xl border border-border/60 px-3 py-2.5">
          <p className="min-w-0 flex-1 text-[11px] leading-snug text-muted-foreground">
            {t('numberHabit.explainer')}
          </p>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="flex shrink-0 cursor-pointer items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary-text transition-colors hover:bg-primary/20"
          >
            <Hash className="size-3" strokeWidth={2.5} />
            {t('numberHabit.chip')}
          </button>
        </div>
      ) : (
        <div className="min-w-0 rounded-xl border border-border/60 p-3">
          <p className={cn(eyebrow, 'mb-1')}>{t('numberHabit.track')}</p>
          <MeasureForm
            draft={draft}
            onChange={setDraft}
            picked={picked}
            onPick={(preset) => {
              setDraft(preset.measure)
              setPicked(preset)
            }}
            {...(saved?.fast && {
              schedule: habit.oneOff
                ? ('once' as const)
                : habit.recursPeriod === 'monthly'
                  ? ('monthly' as const)
                  : habit.recursPeriod === 'weekly'
                    ? ('weekly' as const)
                    : undefined,
              onSchedule: (schedule: FastSchedule) =>
                void setFastSchedule({ habitId, schedule }).catch(() =>
                  toast.error(t('numberHabit.saveError')),
                ),
            })}
          />
          <button
            type="button"
            disabled={!dirty || !isValidAmount(draft.target)}
            onClick={() => save(cleanDraft(draft))}
            className={cn(saveButton, 'mt-3 mb-3')}
          >
            {t('numberHabit.saveMeasure', { label: measureChipLabel(draft, lang) })}
          </button>
          <button
            type="button"
            onClick={() => (saved ? save(null) : setExpanded(false))}
            className={cn(kindSwitch, 'text-muted-foreground hover:text-foreground')}
          >
            {t('numberHabit.checkOffInstead')}
          </button>
        </div>
      )}
    </div>
  )
}

// Composer: one-tap number habits the user doesn't track yet.
export function NumberHabitSuggestions({
  habits,
  onPick,
}: {
  habits: ReadonlyArray<{ title: string; measure?: { unit: string; fast?: boolean } | null }>
  onPick: (preset: MeasurePreset) => void
}) {
  const { t, i18n } = useTranslation()
  const fill = useCategoryFill()
  const presets = [
    ...presetsToSuggest(habits, (id) => ({
      title: t(`numberHabit.presets.${id}.title`),
      unit: t(`numberHabit.presets.${id}.unit`),
    })).map((p) => presetMeasure(t, p.id)),
    ...(habits.some((h) => h.measure?.fast) ? [] : [fastPreset(t, 'fast16')]),
  ]
  if (!presets.length) return null
  return (
    <div
      role="group"
      aria-label={t('numberHabit.suggestions')}
      className="-mx-1 mt-2 flex gap-1.5 overflow-x-auto px-1"
    >
      {presets.map((p) => {
        const Icon = habitIconComponent(p.icon)
        return (
          <button
            key={p.id}
            type="button"
            title={p.desc}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(p)}
            className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-border/60 bg-card/40 px-3 py-1.5 text-xs font-medium text-foreground/80 transition-colors hover:border-border hover:bg-card/60 hover:text-foreground"
          >
            <Icon
              className="size-3.5 shrink-0"
              strokeWidth={2.2}
              style={{ color: fill(CATEGORY_COLORS[p.category]) }}
            />
            {p.title}
            <span className="font-mono text-[11px] text-muted-foreground/60">
              {measureChipLabel(p.measure, i18n.language)}
            </span>
          </button>
        )
      })}
    </div>
  )
}
